import { yupResolver } from '@hookform/resolvers';
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents';
import { Card, CardContent, MenuItem, TextField } from '@material-ui/core';
import React, { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { CommonYupValidation } from 'helpers/yup';
import { SystemMsgs } from 'helpers/SystemMsg';
import { PhoneNumInput } from '@ifca-root/react-component/src/components/Input/PhoneNumInput';
import { AddressAutoCompleteComponent } from '@ifca-root/react-component/src/components/Address/AddressAutoCompleteComponent';
import { useAddressAutoComplete } from 'helpers/hooks/useAddressAutoComplete';
import { countryAlphaCodes } from 'utils/countryAlphaCodes';
import { countryCurrencyCodes } from 'utils/countryCurrencyCodes';
import {
	GetBillDocument,
	useGetCountryNameLazyQuery,
	useGetStateNameLazyQuery,
	useSubmitDocumentEInvoice3rdpartyMutation,
	useUpdateBuyerInformationMutation,
} from 'generated/graphql';
import Loading from '@ifca-root/react-component/src/components/Loading/Loading';
import { useHistory } from 'react-router';
import SnackBarContext from 'containers/App/Store/SnackBarContext';
import { DynamicFooter } from 'components/Footer/DynamicFooter';

interface BillUserEInvoiceFormProps {
	name: string;
	email: string;
	contactNo: string;
	address: string;
	country: string;
	state: string;
	city: string;
	postcode: string;
	identityType: string;
	identityNumber: string;
	tin: string;
	regNo: string;
}

export const BuyerDetailsForm = props => {
	const { data, isBuyerDetails, setIsBuyerDetails } = props;
	const mode = 'edit';
	const editData = data;
	const history = useHistory();

	const { setOpenSnackBar, setSnackBarMsg }: any = useContext(
		SnackBarContext as any,
	);

	const integrate3rdPartyeInvoice = data?.outlet?.integrate3rdPartyeInvoice;

	const [
		loadCountry,
		{
			loading: getCountryLoading,
			data: { getCountryName } = { getCountryName: [] },
		},
	] = useGetCountryNameLazyQuery({
		fetchPolicy: 'network-only',
	});

	const [
		loadState,
		{ loading: getStateLoading, data: { getStateName } = { getStateName: [] } },
	] = useGetStateNameLazyQuery({
		fetchPolicy: 'network-only',
	});

	const [
		updateBuyerInformation,
		{ loading: updateBuyerInformationLoading },
	] = useUpdateBuyerInformationMutation({
		onError: error => {
			console.log('ERROR', error);
		},
		onCompleted: data => {
			setIsBuyerDetails(false);
			setOpenSnackBar(true);
			setSnackBarMsg(SystemMsgs.updateRecord());
		},
	});

	const [
		submitDocumentEInvoice3rdparty,
		{
			loading: submitDocumentEInvoice3rdpartyLoading,
			called: submitDocumentEInvoice3rdpartyCalled,
			error: submitDocumentEInvoice3rdpartyError,
			data: submitDocumentEInvoice3rdpartyDate,
		},
	] = useSubmitDocumentEInvoice3rdpartyMutation({
		onError: error => {
			setOpenSnackBar(true);
			// If error has a message, display it
			setSnackBarMsg(error?.message || 'An unknown error occurred.');
		},
		onCompleted: data => {
			if (data?.submitDocumentEInvoice3rdparty?.status === 'error') {
				const errorDetails =
					data?.submitDocumentEInvoice3rdparty?.message ||
					'Unknown error message';

				// Parse the error details string to extract errorMessages
				try {
					const parsedError = JSON.parse(
						errorDetails.replace('HTTP error! Status: 400, Details: ', ''),
					);

					const errorMessages =
						parsedError?.schemaError?.[0]?.errorMessages ||
						'No specific errors';
					setOpenSnackBar(true);
					setSnackBarMsg(`Submission failed. Error: ${errorMessages}`);
				} catch (parseError) {
					setOpenSnackBar(true);
					setSnackBarMsg('Error parsing the error details.');
				}
			} else if (data?.submitDocumentEInvoice3rdparty?.status === 'success') {
				// For success response (e.g., if status is not 'error')
				setOpenSnackBar(true);
				setSnackBarMsg('Record submitted successfully!');
				setTimeout(() => {
					window.location.reload();
				}, 500);
			}
		},
	});

	const {
		getCountry,
		getStatebyCountry,
		getCitybyState,
		getPostcodebyCity,
		handleCountryOnChange,
		handleStateOnChange,
		handleCityOnChange,
		handlePostCodeOnChange,
		handleGoogleOnChange,
		query,
		autoCompleteRef,
		state,
		setQuery,
		dispatch,
		initializeEditValue,
	} = useAddressAutoComplete();

	const [country, setCountry] = useState('');
	const [newState, setState] = useState('');
	const [city, setCity] = useState('');
	const [newPostcode, setPostcode] = useState('');
	const [countryCode, setCountryCode] = useState('my');
	const [ogAddr, setOGAddr] = useState(null);
	const [addrValue, setAddress] = useState(null);

	useEffect(() => {
		if (getCountry?.length > 0 && mode === 'edit') {
			let currCountry = getCountry?.find(v => {
				return (
					v?.name?.toLowerCase() === editData?.address?.country?.toLowerCase()
				);
			});

			handleCountryOnChange(currCountry);
		}
	}, [getCountry, mode]);

	useEffect(() => {
		const regex = new RegExp(`\\b(${country})\\b`, 'g');
		const countryCodes = Object.keys(countryAlphaCodes);
		if (country !== '') {
			countryCodes.forEach(code => {
				if (regex.test(countryAlphaCodes?.[code])) {
					setCountryCode(code.toLowerCase() ?? 'my');
				}
			});
			let alphaCode;
			const currencyCodes = Object.keys(countryCurrencyCodes);
			currencyCodes.forEach(code => {
				if (regex.test(countryCurrencyCodes?.[code])) {
					alphaCode = code;
				}
			});
		}
	}, [country]);

	useEffect(() => {
		if (mode === 'edit' && editData) {
			let array_of_address = [
				editData?.einvoice_buyer_address,
				editData?.einvoice_buyer_city,
				getStateName[0]?.name || editData?.einvoice_buyer_state,
				getCountryName[0]?.name || editData?.einvoice_buyer_country,
			];
			setQuery(array_of_address.join(', '));
			setOGAddr(array_of_address.join(', '));
			setAddress(array_of_address.slice(0));
			initializeEditValue({
				address: editData?.einvoice_buyer_address,
				country: getCountryName[0]?.name || editData?.einvoice_buyer_country,
				state: getStateName[0]?.name || editData?.einvoice_buyer_state,
				city: editData?.einvoice_buyer_city,
				postCode: editData?.einvoice_buyer_post_code,
			});
		}
	}, [mode, editData, getCountryName[0]?.name, getStateName[0]?.name]);

	const billUserEInvoiceSchema = yup.object().shape({
		name: CommonYupValidation.requireField(SystemMsgs.name()),
		email: yup
			.string()
			.required('Email is required.')
			.trim()
			.test(
				'not-na',
				'"NA" is not allowed.',
				value => value?.toLowerCase() !== 'na',
			)
			.email('Invalid email format.'),
		contactNo: CommonYupValidation.requireField(SystemMsgs.contactNo()),
		regNo: CommonYupValidation.requireField('Registration Number is required'),
		identityNumber: CommonYupValidation.requireField(
			'Identity Number is required',
		),
		identityType: CommonYupValidation.requireField('Identity Type is required'),
		tin: CommonYupValidation.requireField('TIN is required'),
	});

	const {
		handleSubmit,
		register,
		errors,
		control,
		getValues,
		watch,
		formState: { isSubmitting, isSubmitted, dirtyFields, isDirty },
	} = useForm<BillUserEInvoiceFormProps>({
		defaultValues: {
			address: '',
			country: '',
			state: '',
			city: '',
			postcode: '',
		},
		mode: 'onSubmit',
		resolver: yupResolver(billUserEInvoiceSchema),
	});

	useEffect(() => {
		if (editData?.einvoice_buyer_country) {
			loadCountry({
				variables: {
					eInvCode: editData?.einvoice_buyer_country,
				},
			});
		}
	}, [editData?.einvoice_buyer_country]);

	useEffect(() => {
		if (getCountryName[0]?.ID) {
			loadState({
				variables: {
					countryID: getCountryName[0]?.ID,
					eInvCode: editData?.einvoice_buyer_state,
				},
			});
		}
	}, [getCountryName[0]?.ID]);

	const onSubmit = () => {
		if (integrate3rdPartyeInvoice === false) {
			updateBuyerInformation({
				variables: {
					buyerInformation: {
						ID: data?.ID,
						name: watch('name'),
						email: watch('email'),
						contactNo: watch('contactNo')?.replace(/[^+\d]/g, ''),
						identityType: watch('identityType'),
						identityNumber: watch('identityNumber'),
						tin: watch('tin'),
						regNo: watch('regNo'),
						address: {
							address: state?.address,
							country: state?.country?.name,
							state: state?.state?.name,
							city: state?.city?.name,
							postCode: state?.postcode?.postcode,
						},
					},
				},
				refetchQueries: [
					{
						query: GetBillDocument,
						variables: {
							ID: data?.ID,
						},
					},
				],
			});
		} else {
			try {
				submitDocumentEInvoice3rdparty({
					variables: {
						billID: data?.ID,
						outletID: data?.outletID,
						integrate3rdPartyEInvoiceBuyerInput: {
							name: watch('name'),
							contactNo: watch('contactNo'),
							email: watch('email'),
							identityType: watch('identityType'),
							identityNumber: watch('identityNumber'),
							tin: watch('tin'),
							sstRegNo: watch('sstRegNo'),
							address: {
								address: state.address,
								state: state.state.name,
								city: state.city.name,
								country: state.country.name,
								postCode: state.postcode.postcode,
							},
						},
					},
				});
			} catch (error) {
				console.error('Submission failed:', error);
			}
		}
	};

	const identityTypeData = [
		{
			name: 'Identity Card (NRIC)',
			code: data?.outlet?.integrate3rdPartyeInvoice === true ? 'NRIC' : 'NRIC',
		},
		{
			name: 'Passport',
			code:
				data?.outlet?.integrate3rdPartyeInvoice === true
					? 'PASSPORT'
					: 'ForeignBuyer',
		},
		{
			name: 'Business Registration Number',
			code:
				data?.outlet?.integrate3rdPartyeInvoice === true ? 'BRN' : 'Company',
		},
		{ name: 'Military Card', code: 'ARMY' },
	];

	return (
		<>
			{getStateLoading && <Loading />}
			{getCountryLoading && <Loading />}

			<CardContents
				style={{
					overflowY: 'auto',
					maxHeight: 'calc(100vh - 120px)',
					paddingRight: '10px',
				}}
			>
				<Card
					style={{
						backgroundColor: '#f0f0f0',
						boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
						borderRadius: '8px',
						width: '100%',
						marginTop: '1%',
						marginBottom: '4%',
					}}
				>
					<CardContent>
						<div
							style={{
								fontSize: '12px',
								fontWeight: 400,
								lineHeight: '14px',
								fontStyle: 'italic',
								marginTop: '5px',
							}}
						>
							Please complete the following form :
						</div>

						<Controller
							as={TextField}
							name="name"
							fullWidth
							margin="normal"
							label="Name"
							required
							defaultValue={data?.einvoice_buyer_name}
							helperText={errors?.name?.message}
							error={errors?.name ? true : false}
							control={control}
							ref={register}
							autoComplete="nope"
						></Controller>

						<Controller
							as={TextField}
							name="email"
							label="Email Address"
							autoComplete="off"
							multiline={true}
							required
							defaultValue={data?.einvoice_buyer_email}
							fullWidth
							ref={register}
							control={control}
							margin="dense"
							helperText={errors?.email?.message}
							error={errors?.email ? true : false}
						></Controller>

						<Controller
							as={
								<PhoneNumInput
									country={'my'}
									specialLabel="Contact No."
									fullWidth
								/>
							}
							name="contactNo"
							type="tel"
							marginTop
							marginBottom
							required
							defaultValue={data?.einvoice_buyer_contact_no}
							margin="normal"
							control={control}
							ref={register}
							helperText={errors?.contactNo?.message}
							error={errors?.contactNo ? true : false}
							autoComplete="off"
						></Controller>

						<AddressAutoCompleteComponent
							getCountry={getCountry}
							getStatebyCountry={getStatebyCountry}
							getCitybyState={getCitybyState}
							getPostcodebyCity={getPostcodebyCity}
							handleCountryOnChange={handleCountryOnChange}
							handleStateOnChange={handleStateOnChange}
							handleCityOnChange={handleCityOnChange}
							handlePostCodeOnChange={handlePostCodeOnChange}
							handleGoogleOnChange={handleGoogleOnChange}
							autoCompleteRef={autoCompleteRef}
							state={state}
							country={country}
							setCountry={setCountry}
							setState={setState}
							setCity={setCity}
							newState={newState}
							newPostcode={newPostcode}
							setPostcode={setPostcode}
							city={city}
							formType={mode}
							isSubmitted={isSubmitted}
							editData={editData}
						/>
						<Controller
							as={
								<TextField name="identityType">
									{identityTypeData?.map(x => (
										<MenuItem key={x.name} value={x.code}>
											{x.name}
										</MenuItem>
									))}
								</TextField>
							}
							name="identityType"
							margin="normal"
							label="Identity Type"
							fullWidth
							autoComplete="off"
							multiline={true}
							defaultValue={data?.einvoice_buyer_identity_type}
							select
							control={control}
							ref={register}
							required
							helperText={errors?.identityType?.message}
							error={errors?.identityType ? true : false}
						/>
						<Controller
							as={TextField}
							name="identityNumber"
							label="Identity Number"
							autoComplete="off"
							multiline={true}
							required
							fullWidth
							ref={register}
							control={control}
							margin="dense"
							helperText={errors?.identityNumber?.message}
							defaultValue={
								data?.einvoice_buyer_regno
								// ? data.einvoice_buyer_regno.split(': ')[1]
								// : ''
							}
							error={errors?.identityNumber ? true : false}
							inputProps={{
								pattern: '[A-Za-z0-9]*', // Regex to allow only letters and digits
							}}
							onInput={e => {
								// Optional: You can sanitize the input by removing non-alphanumeric characters
								e.target.value = e.target.value.replace(/[^A-Za-z0-9]/g, '');
							}}
						></Controller>

						<Controller
							as={TextField}
							name="tin"
							label="Tax Identification Number (TIN)"
							autoComplete="off"
							multiline={true}
							required
							fullWidth
							ref={register}
							control={control}
							defaultValue={data?.einvoice_buyer_tin}
							margin="dense"
							helperText={errors?.tin?.message}
							error={errors?.tin ? true : false}
						></Controller>

						<Controller
							as={TextField}
							name="regNo"
							label="SST Registration Number"
							autoComplete="off"
							multiline={true}
							// required
							fullWidth
							defaultValue={data?.einvoice_buyer_tax_regno ?? 'NA'}
							ref={register}
							control={control}
							margin="dense"
							helperText={errors?.regNo?.message}
							error={errors?.regNo ? true : false}
						></Controller>
					</CardContent>
				</Card>
			</CardContents>

			<DynamicFooter
				overlap
				options={[
					{
						name: 'Cancel',
						onClick: () => {
							setIsBuyerDetails(false);
						},
						color: 'primary',
					},
					{
						name:
							integrate3rdPartyeInvoice === false ? 'Save' : 'Save & Submit',
						onClick: () => {
							handleSubmit(onSubmit)();
						},
						color: 'primary',
					},
				]}
			/>
		</>
	);
};
